import React, { useState } from "react";
import { useQueryClient } from 'react-query';
import { useSelector } from "react-redux";
import ReactTooltip from 'react-tooltip'
import { useSnackbar } from "notistack";
import moment from "moment";
import 'react-datepicker/dist/react-datepicker.css';

import AssignDevicePanel from "containers/Dashboard/AssignDevicePanel";
import AgrismartMap from "../../components/Map/AgrismartMap";
import { Row, Column } from "../../components/Layout";
import InfoCell from "../../components/Layout/InfoCell";
import Alert from "../../components/Alert/Alert";
import WeatherWidget from "../../components/WeatherWidget";
import NTMTable from "../../components/Table/NTMTable";
import Image from "../../components/Image";
import AgrismartDeviceModal from "../../components/Modal/AgrismartDeviceModal";
import AssignConfigurationModal from "components/Modal/AssignConfigurationModal";
import CloseAlertModal from "../../components/Modal/CloseAlertModal";
import NTMButton from "../../components/Buttons/NTMButton";
import { AGRISMART2, ALERTS, DEVICES, getErrorMessage, post, PROJECTS, put } from "../../service/Client";
import { useGetAlerts, useGetConfigurations, useGetDevices, useGetParcels } from "../../service/Agrismart2Service";
import { roles } from "../../constants";
import { editIcon, editIcon_over, pinIcon, alertIcon_tri as alertIcon } from 'img/icons'
import style from "../Dashboard/style.module.scss"


export default function Agrismart2Dashboard() {

	const [deviceModalOpen, setDeviceModalOpen] = useState()
	const [deviceFields, setDeviceFields] = useState()
	const [closeAlertModalOpen, setCloseAlertModalOpen] = useState()
	const [assignConfigModalOpen, setAssignConfigModalOpen] = useState()
	const [assignConfigDevice, setAssignConfigDevice] = useState()
	const [assignConfigId, setAssignConfigId] = useState()
	const { enqueueSnackbar } = useSnackbar()
	const queryClient = useQueryClient()
	const project = useSelector(store => store.session.project)
	const user = useSelector(store => store.users.data)
	const { status, devices } = useGetDevices()
	const { parcels } = useGetParcels()
	const { alerts } = useGetAlerts(false)
	const { configurations } = useGetConfigurations()

	function showError(e) {
		enqueueSnackbar(getErrorMessage(e), { variant: "error" });
	}

	function devicePopUp(name, ts) {
		return `<div class="bubble">
					<div class="name">id</div><div>${name}</div>
					<div class="name">ultimo contatto</div><div>${moment(ts).format("DD-MM-YYYY HH:mm")}</div>
				</div>`;
	}


	function saveDevice(deviceProps) {
		setDeviceModalOpen(false)
		setDeviceFields(null)
		put(`${AGRISMART2}/${DEVICES}/${deviceProps.device}`, { body: deviceProps })
			.then(() => queryClient.invalidateQueries('devices'))
			.catch(showError);
	}

	function closeAlert(silUntil) {
		post(`${AGRISMART2}/${ALERTS}/${closeAlertModalOpen}/close`, { params: { silUntil } })
			.then(() => queryClient.invalidateQueries('alerts'))
			.catch(showError);
		setCloseAlertModalOpen(null);
	}

	function disassociate(device) {
		put(`${PROJECTS}/${project.id}/devices`, { body: devices.filter(d => d.device !== device.device).map(d => d.device) })
			.then(() => queryClient.invalidateQueries('devices'))
			.catch(showError);
	}

	function assignConfig(deviceSerial, configId) {
		put(`${AGRISMART2}/devices/${deviceSerial}/configuration`, { body: { id: configId } })
			.then(() => queryClient.invalidateQueries('devices'))
			.catch(showError)
	}

	function getValue(field) {
		return field || field === 0 ? `${+field.toFixed(2)}` : "-"
	}

	if (status === 'loading') return <div />

	let markers = [];
	let allParameters = [];

	devices.forEach(d => {
		d.lat && markers.push({
			lat: d.lat,
			lng: d.lng,
			data: devicePopUp(d.device, d.lastSeen),
			icon: pinIcon
		});
		allParameters.push(...(d?.configuration?.parameters ?? []))
	});
	// remove duplicate params
	allParameters = [...new Map(allParameters.map((p) => [p.name, p])).values()]
	allParameters.sort((a, b) => a.name.localeCompare(b.name))

	let alertBoxes = alerts
		.filter(a => moment(a.openTime).isAfter(moment().subtract(10, 'days')))
		.sort((a, b) => b.openTime - a.openTime)
		.slice(0, 5)
		.map(a => {
			let dateLabel = moment(a.openTime).tz("Europe/Rome").format("DD/MM/YYYY HH:mm")
			let title = `${dateLabel} - Rilevata ${a.typeLabel} (${a.deviceLabel || a.device})`
			return {
				id: a.id,
				title,
				onSolve: () => setCloseAlertModalOpen(a.id)
			}
		})

	let header = [
		{
			Header: "ID",
			accessor: 'device',
			Cell: props => <Column>
				<Row style={{ justifyContent: "space-around" }}>
					<span style={{ paddingRight: "0.5rem" }}>{props.original.device}</span>
				</Row>
				<Row>
					{roles.isAdmin(user) && <NTMButton primary onClick={() => { setDeviceModalOpen(true); setDeviceFields(props.original); }}>Modifica</NTMButton>}
					{roles.isSuperUser(user) && <NTMButton primary onClick={() => disassociate(props.original)}>Rimuovi</NTMButton>}
				</Row>
			</Column>
		},
		{
			Header: "Tipo",
			accessor: 'type',
			Cell: props => <Row style={{justifyContent: "space-around"}}>
				<span style={{paddingRight: "0.5rem"}}>{props.original.typeName || "-"}</span>
			</Row>
		},
		{
			Header: "Configurazione",
			accessor: 'configuration',
			Cell: props => <Column>
				<Row style={{ justifyContent: "space-around" }}>
					{
						!!props.original?.configuration?.code && !props.original?.configuration?.tookEffectAt &&
							<div className={style.alert}>
								<div data-tip={"E' richiesto il riavvio del dispositivo"}>
									<img src={alertIcon} alt="alert" style={{width:"20px", height:"20px"}}/>
								</div>
								<ReactTooltip className={style.tooltip} effect='solid'/>
							</div>
					}
					<span style={{ paddingRight: "0.5rem" }}>{props.original?.configuration?.code ?? 'non assegnato'}</span>
					<Image
						src={editIcon} hover={editIcon_over}
						onClick={() => {
							setAssignConfigDevice(props.original?.device)
							setAssignConfigId(props.original?.configuration?.id)
							setAssignConfigModalOpen(true)
						}}
					/>
				</Row>
			</Column>
		},
		{
			Header: "Label",
			accessor: 'label',
			Cell: props => <Row style={{ justifyContent: "space-around" }}>
				<span style={{ paddingRight: "0.5rem" }}>{props.original.label || props.original.device}</span>
			</Row>
		},
		{
			Header: "Ultima ril",
			accessor: 'lastSeen',
			Cell: props =>
				<div>{moment(props.original.lastSeen).tz("Europe/Rome").format("DD/MM/YYYY HH:mm")}</div>
		},
		{
			Header: "Batteria",
			accessor: 'battery',
			Cell: props => <div>{`${props.original.battery ?? "-"} %`}</div>
		},
		...allParameters.filter(p => p.type !== 'CALCULATED').map(p => ({
			Header: p.labelShort,
			accessor: p.name,
			Cell: props => <div>{`${getValue(props.original.lastData && props.original.lastData[p.name])} ${p.unit}`}</div>
		}))
	]

	// nascondo la colonna configuration
	if (!roles.isSuperUser(user)) {
		header.splice(1, 1)
	}

	return (
		<div className={"animated fadeIn container"}>
			<Row className={style.box}>
				<AgrismartMap parcels={parcels} markers={markers} devices={devices} />
			</Row>

			<Row>
				{
					devices.length === 0 && <Row style={{ color: "#D8232A", paddingBottom: "1rem", fontWeight: "bold" }}>Non è ancora stato associato nessun dispositivo a questo progetto</Row>
				}
				{
					devices.length === 1 && <Column style={{ flex: "1 0 auto" }}>
						<div className={style.infoGrid}>
							<InfoCell name={"ID Centralina"} value={devices[0].device} />
							{
								roles.isSuperUser(user) &&
									<InfoCell name={"Configurazione"}
										alarmMessage={!!devices[0]?.configuration?.code && !devices[0]?.configuration?.tookEffectAt && "E' richiesto il riavvio del dispositivo"}
										value={devices[0]?.configuration?.code ?? 'non assegnato'}
										onEdit={() => {
											setAssignConfigDevice(devices[0]?.device)
											setAssignConfigId(devices[0]?.configuration?.id)
											setAssignConfigModalOpen(true)
										}}
									/>
							}
							<InfoCell name={"Tipo"} value={devices[0].typeName} />
							<InfoCell name={"Label"} value={devices[0].label || devices[0].device} />
							<InfoCell name={"Ultima rilevazione"} value={moment(devices[0].lastSeen).format("DD/MM/YYYY - HH:mm")} />
							<InfoCell name={"Batteria"} value={devices[0].battery} um={"%"} />
							{
								allParameters.filter(p => p.type !== 'CALCULATED').map(p =>
									<InfoCell key={p.name}
										name={p.labelLong}
										value={getValue(devices[0].lastData && devices[0].lastData[p.name])}
										um={p.unit}
									/>
								)
							}
						</div>
						<Row>
							{roles.isAdmin(user) &&	<NTMButton primary onClick={() => {setDeviceModalOpen(true); setDeviceFields(devices[0])}}>Modifica</NTMButton>}
							{roles.isSuperUser(user) && <NTMButton primary onClick={() => disassociate(devices[0])}>Rimuovi</NTMButton>}
						</Row>
					</Column>
				}
				{
					devices.length > 1 &&
					<NTMTable
						header={header}
						body={devices}
						defaultPageSize={5}
					/>
				}

				<Column style={devices.length === 1 ? { flex: "0 0 280px" } : undefined}>
					{
						alertBoxes.map((a, i) => <Alert key={i} title={a.title} onSolve={roles.isAdmin(user) && a.onSolve} />)
					}
				</Column>
			</Row>

			{devices.length > 0 &&
				<WeatherWidget
					lat={devices[0].lat}
					lng={devices[0].lng}
					location={devices[0].device}
					type="forecast"
				/>
			}
			<AssignDevicePanel devices={devices.map(d => d.device)} />

			<AssignConfigurationModal
				isOpen={assignConfigModalOpen}
				onClose={() => {setAssignConfigModalOpen(null)}}
				onSubmit={() => {assignConfig(assignConfigDevice, assignConfigId); setAssignConfigModalOpen(null)}}
				device={assignConfigDevice}
				assignedConfigId={assignConfigId}
				setAssignedConfigId={setAssignConfigId}
				allConfigs={configurations}
			/>
			<AgrismartDeviceModal
				isOpen={deviceModalOpen}
				onClose={() => setDeviceModalOpen(false)}
				onSubmit={saveDevice}
				deviceFields={deviceFields}
			/>
			<CloseAlertModal
				isOpen={!!closeAlertModalOpen}
				onClose={() => setCloseAlertModalOpen(!closeAlertModalOpen)}
				onSubmit={closeAlert}
			/>
		</div>
	);

}
